import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  LineChart, Line, CartesianGrid, XAxis, YAxis,
  BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ScatterChart, Scatter, ZAxis,
  Treemap,
  AreaChart, Area, ReferenceLine,
  Sankey, Layer, Rectangle,
} from "recharts";
import { useMemo } from "react";

const COLORS = {
  marine: "#1A3C5E", gold: "#C9A84C", alert: "#C0392B",
  warn: "#E67E22", success: "#27AE60", slate: "#94A3B8",
};
const PALETTE = [COLORS.marine, COLORS.gold, COLORS.alert, COLORS.warn, COLORS.success, COLORS.slate];

const tooltipStyle = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 4, fontSize: 12, padding: 8, color: "hsl(var(--foreground))",
};

export function DebtCompositionDonut({ composition }) {
  const data = useMemo(() => {
    if (!composition) return [];
    return Object.entries(composition)
      .filter(([, v]) => v && v.amount)
      .map(([k, v]) => ({ name: labelize(k), value: v.amount, pct: v.pct }));
  }, [composition]);

  if (data.length === 0) {
    return <EmptyChart label="Aucune donnée de composition de dette" />;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={1}>
          {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v, n, p) => [`${formatMoney(v)} (${p.payload.pct}%)`, n]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function DebtGdpTrajectory({ currentDebtGdp, projection }) {
  const base = currentDebtGdp ? [{ year: "T0", value: currentDebtGdp, type: "actuel" }] : [];
  const data = base.concat((projection || []).map((p) => ({
    year: `T+${p.year_offset}`, value: p.debt_gdp, type: "projection",
  })));
  if (data.length === 0) return <EmptyChart label="Aucune trajectoire disponible" />;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
        <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" unit="%" />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${Number(v).toFixed(1)}%`} />
        <ReferenceLine y={70} stroke={COLORS.alert} strokeDasharray="4 2" label={{ value: "Seuil 70%", fontSize: 10, fill: COLORS.alert }} />
        <ReferenceLine y={55} stroke={COLORS.warn} strokeDasharray="4 2" label={{ value: "Seuil 55%", fontSize: 10, fill: COLORS.warn }} />
        <Line type="monotone" dataKey="value" stroke={COLORS.marine} strokeWidth={2.5} dot={{ r: 4, fill: COLORS.gold }} activeDot={{ r: 6 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function CreditorsBar({ clauses }) {
  const data = useMemo(() => {
    if (!clauses || clauses.length === 0) return [];
    const agg = {};
    clauses.forEach((c) => {
      const name = c.creditor || "Inconnu";
      agg[name] = (agg[name] || 0) + (c.amount || 0);
    });
    return Object.entries(agg)
      .filter(([, v]) => v > 0)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  }, [clauses]);

  if (data.length === 0) return <EmptyChart label="Aucune convention de prêt extraite" />;
  return (
    <ResponsiveContainer width="100%" height={Math.max(240, data.length * 38)}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={formatMoneyShort} />
        <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={120} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatMoney(v)} />
        <Bar dataKey="amount" fill={COLORS.marine} radius={[0, 2, 2, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function SectorRadar({ social }) {
  const d = [
    { sector: "Santé", value: social?.access_to_healthcare_pct ?? 0 },
    { sector: "Éducation", value: social?.access_to_education_pct ?? social?.literacy_rate ?? 0 },
    { sector: "Emploi", value: social?.unemployment_rate ? 100 - social.unemployment_rate : 0 },
    { sector: "Pauvreté (inv.)", value: social?.poverty_rate ? 100 - social.poverty_rate : 0 },
    { sector: "Vie", value: social?.life_expectancy ? social.life_expectancy : 0 },
    { sector: "Alphab.", value: social?.literacy_rate ?? 0 },
  ];
  return (
    <ResponsiveContainer width="100%" height={280}>
      <RadarChart data={d}>
        <PolarGrid stroke="hsl(var(--border))" />
        <PolarAngleAxis dataKey="sector" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
        <Radar dataKey="value" stroke={COLORS.marine} fill={COLORS.gold} fillOpacity={0.4} />
        <Tooltip contentStyle={tooltipStyle} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

export function BudgetTreemap({ macro, debt, vit }) {
  const data = [
    { name: "Service de la dette", value: debt?.debt_service_total || 0, fill: COLORS.alert },
    { name: "Dépenses publiques", value: Math.max((macro?.public_expenditure || 0) - (debt?.debt_service_total || 0), 0), fill: COLORS.marine },
    { name: "Intérêts", value: debt?.interest_payments || 0, fill: COLORS.warn },
    { name: "Principal", value: debt?.principal_repayments || 0, fill: "#7A1F15" },
    { name: "Espace social", value: Math.max(vit?.espace_social || 0, 0), fill: COLORS.success },
  ].filter((x) => x.value > 0);
  if (data.length === 0) return <EmptyChart label="Données budgétaires insuffisantes" />;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <Treemap data={data} dataKey="value" stroke="hsl(var(--background))" fill={COLORS.marine}>
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatMoney(v)} />
      </Treemap>
    </ResponsiveContainer>
  );
}

export function OddScatter({ conditionalities }) {
  const data = (conditionalities || []).map((c, i) => ({
    x: severityScore(c.type),
    y: impactScore(c.impact_on_social_spending),
    z: 20,
    name: c.description?.slice(0, 50) || c.type,
  }));
  if (data.length === 0) return <EmptyChart label="Aucune conditionnalité extraite" />;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
        <XAxis dataKey="x" name="Sévérité" type="number" domain={[0, 5]} tick={{ fontSize: 11 }} label={{ value: "Sévérité (1-5)", position: "insideBottom", offset: -5, fontSize: 10 }} />
        <YAxis dataKey="y" name="Impact social" type="number" domain={[0, 5]} tick={{ fontSize: 11 }} label={{ value: "Impact social (1-5)", angle: -90, position: "insideLeft", fontSize: 10 }} />
        <ZAxis dataKey="z" range={[60, 120]} />
        <Tooltip contentStyle={tooltipStyle} cursor={{ strokeDasharray: "3 3" }} />
        <Scatter data={data} fill={COLORS.alert} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

export function EspaceSocialArea({ macro, debt, vit }) {
  const revenue = macro?.public_revenue || 0;
  const service = debt?.debt_service_total || 0;
  if (!revenue && !service) return <EmptyChart label="Données insuffisantes pour l'espace social" />;
  const data = Array.from({ length: 6 }).map((_, i) => ({
    year: `T${i}`,
    recettes: revenue * (1 + i * 0.03),
    service: service * (1 + i * 0.06),
  }));
  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
        <defs>
          <linearGradient id="gEsp" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLORS.success} stopOpacity={0.5} />
            <stop offset="100%" stopColor={COLORS.success} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
        <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={formatMoneyShort} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatMoney(v)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Area type="monotone" dataKey="recettes" stroke={COLORS.marine} fill="url(#gEsp)" name="Recettes" strokeWidth={2} />
        <Area type="monotone" dataKey="service" stroke={COLORS.alert} fill={COLORS.alert} fillOpacity={0.15} name="Service de la dette" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/** Circular gauge as SVG, 0-100 risk score */
export function RiskGauge({ value, label = "Indice de risque" }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const color =
    v >= 85 ? COLORS.alert :
    v >= 65 ? "#7A1F15" :
    v >= 40 ? COLORS.warn :
    COLORS.success;
  const circumference = 2 * Math.PI * 60;
  const arcLen = (v / 100) * 0.75 * circumference;

  return (
    <div className="flex flex-col items-center justify-center py-4" data-testid="risk-gauge">
      <svg width="200" height="140" viewBox="0 0 200 140">
        <circle cx="100" cy="100" r="60" stroke="hsl(var(--muted))" strokeWidth="10" fill="none"
                strokeDasharray={`${0.75 * circumference} ${circumference}`}
                strokeLinecap="round"
                transform="rotate(135 100 100)" />
        <circle cx="100" cy="100" r="60" stroke={color} strokeWidth="10" fill="none"
                strokeDasharray={`${arcLen} ${circumference}`}
                strokeLinecap="round"
                transform="rotate(135 100 100)"
                style={{ transition: "stroke-dasharray 600ms ease" }} />
        <text x="100" y="95" textAnchor="middle" className="font-mono" fontSize="28" fontWeight="600" fill={color}>
          {v.toFixed(0)}
        </text>
        <text x="100" y="115" textAnchor="middle" fontSize="10" fill="hsl(var(--muted-foreground))">
          / 100
        </text>
      </svg>
      <div className="label-mono text-[10px] mt-1 text-muted-foreground">{label}</div>
    </div>
  );
}

// ========== helpers ==========
function formatMoney(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)} B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)} M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)} K`;
  return n.toFixed(0);
}
function formatMoneyShort(v) { return formatMoney(v); }

function labelize(k) {
  return ({ multilaterale: "Multilatérale", bilaterale: "Bilatérale", commerciale: "Commerciale", domestique: "Domestique" })[k] || k;
}
function severityScore(type) {
  const map = {
    social_spending_cut: 5, privatisation: 4, fiscal_consolidation: 3,
    structural_reform: 3, trade_liberalization: 3, monetary_policy: 2,
    governance_reform: 1, other: 2,
  };
  return map[type] ?? 2;
}
function impactScore(impact) {
  return ({ negative: 5, neutral: 3, positive: 1, inconnu: 2 })[impact] ?? 3;
}
function EmptyChart({ label }) {
  return (
    <div className="h-[280px] grid place-items-center border border-dashed border-border rounded-sm text-xs text-muted-foreground">
      {label}
    </div>
  );
}

// ========== G4 — Waterfall: Décomposition service dette ==========
export function DebtServiceWaterfall({ debt }) {
  if (!debt) return <EmptyChart label="Données de service insuffisantes" />;
  const principal = debt.principal_repayments || 0;
  const interest = debt.interest_payments || 0;
  const service = debt.debt_service_total || (principal + interest);
  const residual = Math.max(service - principal - interest, 0);
  if (service === 0) return <EmptyChart label="Aucun service de la dette détecté" />;
  // Stacked bar trick: base (invisible) + value
  const data = [
    { name: "Principal", base: 0, value: principal, fill: COLORS.marine },
    { name: "Intérêts", base: principal, value: interest, fill: COLORS.warn },
    { name: "Commissions/frais", base: principal + interest, value: residual, fill: COLORS.gold },
    { name: "Total service", base: 0, value: service, fill: COLORS.alert },
  ].filter((d) => d.value > 0);
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={formatMoneyShort} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatMoney(v)} />
        <Bar dataKey="base" stackId="a" fill="transparent" />
        <Bar dataKey="value" stackId="a" radius={[2, 2, 0, 0]}>
          {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ========== G9 — Calendar heatmap: Murs de la dette ==========
export function DebtCalendarHeatmap({ debt, revenue, baseYear }) {
  // Generate 10 years × 4 quarters with synthetic distribution based on total service
  // In absence of a real maturity schedule, we simulate peaks at year+3, year+7 (common refinancing patterns)
  const service = debt?.debt_service_total || 0;
  const yearBase = baseYear || new Date().getFullYear();
  if (!service) return <EmptyChart label="Aucun échéancier disponible" />;
  const cells = [];
  const peaks = new Set([3, 7]); // peak years
  let maxVal = 0;
  for (let y = 0; y < 10; y++) {
    for (let q = 0; q < 4; q++) {
      const isPeak = peaks.has(y);
      const base = service / 10; // linear distribution
      const amt = isPeak ? base * 1.8 / 4 : base * 0.85 / 4;
      const jitter = 0.85 + (((y * 17 + q * 3) % 10) / 10) * 0.3;
      const value = amt * jitter;
      maxVal = Math.max(maxVal, value);
      cells.push({ year: yearBase + y, quarter: q + 1, value });
    }
  }
  const threshold = revenue ? revenue * 0.20 / 4 : maxVal * 0.75; // 20% of quarterly revenue = wall
  return (
    <div className="h-[280px] overflow-auto">
      <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(11, minmax(0, 1fr))" }}>
        <div></div>
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="label-mono text-[9px] text-muted-foreground text-center">
            {yearBase + i}
          </div>
        ))}
        {["Q1", "Q2", "Q3", "Q4"].map((q, qi) => (
          <>
            <div key={`lbl-${q}`} className="label-mono text-[9px] text-muted-foreground flex items-center">{q}</div>
            {Array.from({ length: 10 }, (_, y) => {
              const cell = cells[y * 4 + qi];
              const ratio = cell.value / maxVal;
              const isWall = cell.value > threshold;
              const color = isWall
                ? `rgba(192, 57, 43, ${0.35 + ratio * 0.65})`
                : `rgba(26, 60, 94, ${0.15 + ratio * 0.65})`;
              return (
                <div
                  key={`${y}-${qi}`}
                  title={`${cell.year} ${q}: ${formatMoney(cell.value)}${isWall ? " — MUR" : ""}`}
                  className="aspect-square rounded-sm border border-border/40"
                  style={{ backgroundColor: color, minHeight: 18 }}
                />
              );
            })}
          </>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: "rgba(26,60,94,0.5)" }}></span> Remboursement normal</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: "rgba(192,57,43,0.8)" }}></span> Mur de la dette (>20% recettes trim.)</span>
      </div>
    </div>
  );
}

// ========== G11 — Sankey: Flux créanciers → secteurs ==========
export function DebtFlowSankey({ composition, clauses }) {
  const hasComposition = composition && Object.values(composition).some((v) => v?.amount);
  const hasClauses = clauses && clauses.length > 0;
  if (!hasComposition && !hasClauses) return <EmptyChart label="Flux de dette non calculables" />;

  // Build nodes + links
  const nodes = [];
  const links = [];
  const addNode = (name) => {
    let idx = nodes.findIndex((n) => n.name === name);
    if (idx === -1) { nodes.push({ name }); idx = nodes.length - 1; }
    return idx;
  };
  addNode("DETTE PUBLIQUE");
  const srcIdx = 0;

  if (hasComposition) {
    Object.entries(composition).forEach(([k, v]) => {
      if (!v?.amount) return;
      const label = { multilaterale: "Multilatéral", bilaterale: "Bilatéral", commerciale: "Commercial", domestique: "Domestique" }[k] || k;
      const idx = addNode(label);
      links.push({ source: srcIdx, target: idx, value: v.amount });
    });
  }

  if (hasClauses) {
    const bySector = {};
    clauses.forEach((c) => {
      const sector = c.creditor ? (hasComposition ? "Commercial" : "Créanciers") : "Divers";
      const label = (c.clause_type || "Financement").replace(/_/g, " ");
      bySector[label] = (bySector[label] || 0) + (c.amount || 0);
    });
    Object.entries(bySector).forEach(([k, v]) => {
      if (!v) return;
      const src = nodes.findIndex((n) => n.name === "Commercial");
      const target = addNode(`→ ${k}`);
      links.push({ source: src >= 0 ? src : 0, target, value: v });
    });
  }

  if (links.length === 0) return <EmptyChart label="Données insuffisantes pour le Sankey" />;

  return (
    <ResponsiveContainer width="100%" height={320}>
      <Sankey
        data={{ nodes, links }}
        node={<SankeyNode />}
        link={{ stroke: COLORS.gold, strokeOpacity: 0.45 }}
        nodePadding={20}
        margin={{ top: 10, right: 120, bottom: 10, left: 20 }}
      >
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatMoney(v)} />
      </Sankey>
    </ResponsiveContainer>
  );
}

function SankeyNode({ x, y, width, height, index, payload, containerWidth }) {
  const color = index === 0 ? COLORS.marine : index % 2 ? COLORS.gold : COLORS.alert;
  const isLeft = x + width / 2 < containerWidth / 2;
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.85} />
      <text
        x={isLeft ? x + width + 6 : x - 6}
        y={y + height / 2 + 3}
        fontSize={10}
        textAnchor={isLeft ? "start" : "end"}
        fill="hsl(var(--foreground))"
      >
        {payload.name} · {formatMoney(payload.value)}
      </text>
    </Layer>
  );
}
